import {
    game
} from "../Game";
import * as emitterData from "../data/emitterData";
import { Settings } from "../Settings";
import { AnimatedParticle, SuperEmitter as Emitter } from './SuperEmitter';
import { disableCulling } from './PIXICuller';
import {getCharacterBloodColors} from './BloodColors.js';
import {DEFAULT_BLOOD_COLOR, RAINBOW_BLOOD_COLORS, RAINBOW_BLOOD_INTERVAL} from '../../static/mod/bloodColors.js';
import {neutralBloodTexture} from './bloodTexture.js';
import {beginSplatFrame, endSplatFrame, resetSplats, splatCollider} from './paint/effectSplats.js';

import * as PIXI from 'pixi.js';


let emitters = [];
let emittersPool = {};
let globalBody = {
    emitterCount: 0
};
let activeSplashEmitters = 0;
let bloodTime = 0;
const bloodEmitterTypes = new Set(['blood', 'bloodSpray', 'gorecloud', 'bloodRing', 'bloodDrip']);
const fallingEmitterTypes = new Set(['blood', 'bloodRing', 'juiceRing', 'bloodDrip', 'juiceDrip', 'paintBurst', 'paintSplash']);
const paintEmitterTypes = new Set(['paintBurst', 'paintSplash']);
const splatEmitterTypes = new Set(['blood', 'bloodSpray', 'bloodRing', 'bloodDrip', 'juiceRing', 'juiceDrip', ...paintEmitterTypes]);
// Their drops stain characters in their own colour rather than as blood.
const ownColorEmitterTypes = new Set(['juiceRing', 'juiceDrip', ...paintEmitterTypes]);

function configureBloodColors(emitter, force = false) {
    const rainbow = !Settings.bloodEnabled;
    const colors = getCharacterBloodColors(emitter.body?.bloodSkin);
    if (!force && emitter.bloodRainbow === rainbow && emitter.bloodColors === colors) return;
    emitter.bloodRainbow = rainbow;
    emitter.bloodColors = colors;
    if (emitter.type === 'gorecloud') {
        const original = PIXI.Texture.from('gore-cloud.png');
        const originalColor = !rainbow && colors.start === DEFAULT_BLOOD_COLOR;
        emitter.textures = [originalColor ? original : neutralBloodTexture(original)];
        if (originalColor) {
            emitter.setColors('#ffffff');
            return;
        }
    }
    if (rainbow) emitter.setColorCycle(RAINBOW_BLOOD_COLORS, RAINBOW_BLOOD_INTERVAL, bloodTime);
    else emitter.setColors(colors.start, colors.end);
}

export const init = function () {
    const emitterPoolData = [{
        type: 'blood',
        poolSize: 12
    },
    {
        type: 'bloodSpray',
        poolSize: 6
    },
    {
        type: 'gorecloud',
        poolSize: 8
    },
    {
        type: 'paintBurst',
        poolSize: 2
    },
    {
        type: 'paintSplash',
        poolSize: 2
    },
    {
        type: 'screenConfetti',
        poolSize: 3
    },
    {
        type: 'confetti',
        poolSize: 3
    },
    {
        type: 'splash',
        poolSize: Settings.maxSplashEmitters
    }];
    emitterPoolData.forEach((data) => {
        for (let i = 0; i < data.poolSize; i++) getEmitter(data.type, true, true);
    })
    for (let i = 0; i < emitters.length; i++) {
        emittersPool[emitters[i].type].push(emitters[i]);
    }
}
export const playOnceEmitter = function (type, body, point, angle, randomColors) {
    if(bloodEmitterTypes.has(type) && !Settings.goreEnabled) return;
    if(type === 'splash' && activeSplashEmitters >= Settings.maxSplashEmitters) return;
    if (!angle) angle = 0;
    if (!body) body = globalBody;


    const maxEmitters = body === globalBody ? 30 : Settings.emittersPerBody;
    if (body && body.emitterCount && body.emitterCount >= maxEmitters) return;


    let emitter = getEmitter(type);
    if(!emitter) return;
    emitter.spawnPos.set(point.x * Settings.PTM, point.y * Settings.PTM);

    if(fallingEmitterTypes.has(type)){
        emitter.acceleration.x = game.world.GetGravity().x * 200;
        emitter.acceleration.y = game.world.GetGravity().y * 200;

    }

    attachEmitter(body, emitter);

    if (body) {
        emitter.body = body;
        if (!body.emitterCount) body.emitterCount = 0;
        body.emitterCount++;
    }
    if(type === 'splash') activeSplashEmitters++;

    function returnToPool() {
        if (emitter.body) {
            emitter.body.emitterCount--;
            emitter.body = undefined;
        }
        emitter.lastUsed = Date.now();
        emitter._completeCallback = undefined;
        emittersPool[emitter.type].push(emitter);
        if(type === 'splash') activeSplashEmitters = Math.max(0, activeSplashEmitters - 1);

    }
    // Emitter rotation is expressed in degrees; startRotation remains the
    // emitter config's local particle spread.
    emitter.rotation = angle;

    if(randomColors) {
        //console.warn("v5 + version of pixi particle remove this method");
        emitter.setRandomColors(randomColors);
    }
    if (bloodEmitterTypes.has(type)) configureBloodColors(emitter, true);

    emitter.playOnce(returnToPool);

    return emitter;
}

export const getLoopingEmitter = function(type,body,point,angle){
    if (!angle) angle = 0;

    let emitter = getEmitter(type, false);
    if(!emitter) return;
    emitter.spawnPos.set(point.x * Settings.PTM, point.y * Settings.PTM);

    attachEmitter(body, emitter);

    emitter.rotation = angle;

    return emitter;
}


const attachEmitter = (body, emitter)=>{
    if((body && body.mySprite) || emitter.useParticleContainer){
        // create container - postion container at right index
        // make sure to delete container on reset!!
        if(!emitter.container){
            emitter.container = emitter.useParticleContainer
                ? new PIXI.ParticleContainer({
                    dynamicProperties: {
                        position: true,
                        rotation: true,
                        vertex: true,
                        // Non-animated particles select their atlas frame at
                        // spawn. Add/remove already refreshes static buffers.
                        uvs: emitter.animated,
                        color: true,
                    },
                })
                : new PIXI.Container();
            if(!emitter.useParticleContainer){
                // Particles come and go every frame: keep that from rebuilding the level's render group.
                emitter.container.enableRenderGroup();
                emitter.container.hideWhenEmpty = true;
                emitter.container.visible = false;
            }
            emitter.parent.addChild(emitter.container)
            emitter.parent = emitter.container;
            // ParticleContainer deliberately has no calculated bounds. Particle
            // effects are short-lived, so rendering them beats a costly/incorrect
            // dynamic subtree bounds pass in the world culler.
            disableCulling(emitter.container);
        }

        if(body && body.mySprite){
            const targetDisplay = body.myTexture || body.mySprite;
            const targetParent = targetDisplay.parent;
            if(targetParent){
                const targetIndex = targetParent.getChildIndex(targetDisplay);
                targetParent.addChildAt(emitter.container, targetIndex + 1);
            }
        }else{
            // Global gore clouds still use the lightweight particle path.
            game.editor.textures.addChild(emitter.container);
        }

    }else if(emitter.container){
        const oldContainer = emitter.container;
        emitter.parent = oldContainer.parent || game.editor.textures;
        if(oldContainer.parent) oldContainer.parent.removeChild(oldContainer);
        oldContainer.destroy({children: false});
        delete emitter.container;
    }
}

export const destroyEmitter = emitter =>{
    if(!emitter) return;
    if(emitter.container){
        const oldContainer = emitter.container;
        emitter.parent = oldContainer.parent || game.editor.textures;
        if(oldContainer.parent) oldContainer.parent.removeChild(oldContainer);
        delete emitter.container;
        oldContainer.destroy({children: false});
    }
    emitter.destroy();
}

const ignorePool = {
    "jetfire": true,
    "cannonShoot": true,
    "sparksMetal": true
}

export const getEmitter = function (type, pool = true, init = false) {
    if(pool){
        if (!emittersPool[type]) emittersPool[type] = [];
        if (emittersPool[type].length > 0) return emittersPool[type].pop();
        // some particles ignore the pool
        if(!init && ignorePool[type]) return;
    }

    let emitter;
    switch (type) {
        case "blood":
            emitter = new Emitter(
                game.editor.textures, [
                    PIXI.Texture.from('particle.png'), 
                    PIXI.Texture.from('particle-grey.png')
                ],
                emitterData[type]
            );
            break;

        case "bloodSpray":
            emitter = new Emitter(
                game.editor.textures, [
                    PIXI.Texture.from('particle.png'), 
                    PIXI.Texture.from('particle-grey.png')
                ],
                emitterData[type]
            );
            break;
        case "bloodRing":
        case "juiceRing":
            emitter = new Emitter(
                game.editor.textures, [
                    PIXI.Texture.from('particle.png'),
                    PIXI.Texture.from('particle-grey.png')
                ],
                emitterData.bloodRing
            );
            break;
        case "paintBurst":
        case "paintSplash":
            emitter = new Emitter(
                game.editor.textures, [PIXI.Texture.from('particle.png')],
                emitterData[type]
            );
            emitter.paint = true;
            break;
        case "bloodDrip":
        case "juiceDrip":
            emitter = new Emitter(
                game.editor.textures, [
                    PIXI.Texture.from('particle.png'),
                    PIXI.Texture.from('particle-grey.png')
                ],
                emitterData.bloodDrip
            );
            break;
        case "gorecloud":
            emitter = new Emitter(
                game.editor.textures, [PIXI.Texture.from('gore-cloud.png')],
                emitterData[type]
            );
            break;
         case "jetfire":
            emitter = new Emitter(
                game.editor.textures, [PIXI.Texture.from('particle.png'), PIXI.Texture.from('particle-fire.png')],
                emitterData[type]
            );
            break;
        case "cannonShoot":
            emitter = new Emitter(
                game.editor.textures, [PIXI.Texture.from('particle.png')],
                emitterData[type]
            );
            break;
        case "sparksMetal":
        case "grindSparks":
            emitter = new Emitter(
                game.editor.textures, [PIXI.Texture.from('particle-spark.png')],
                emitterData[type]
            );
            break;
        case "confetti":
            emitter = new Emitter(
                game.editor.textures,
                emitterData.confettiFrames,
                emitterData['confetti']
            );
            emitter.particleConstructor = AnimatedParticle;
            break;
        case "screenConfetti":
            emitter = new Emitter(
                game.stage,
                emitterData.confettiFrames,
                emitterData['confetti']
            );
            emitter.particleConstructor = AnimatedParticle;
            break;
        case "splash":
            emitter = new Emitter(
                game.editor.textures, [PIXI.Texture.from('Splash')],
                emitterData[type]
            );
            break;
    }
    if(pool){
        emitter.type = type;
        emitters.push(emitter);
    }
    if(splatEmitterTypes.has(type)){
        emitter.collider = splatCollider;
        emitter.landGrace = 2.5;
        emitter.ownColor = ownColorEmitterTypes.has(type);
    }
    return emitter;
}
export const update = function () {
    beginSplatFrame();

    for (var i = 0; i < emitters.length; i++) {
        var emitter = emitters[i];
        if (!emitter.body && emittersPool[emitter.type]) {
            // if (Date.now() - emitter.lastUsed > Settings.emitterMaxPoolTime) {
            //     for (var j = 0; j < emittersPool[emitter.type].length; j++){
            //         if (emittersPool[emitter.type][j] == emitter){
            //             emittersPool[emitter.type].splice(j, 1);
            //         }
            //     }
            //     emitter.destroy();
            //     emitters.splice(i, 1);
            //     i--;
            // }
        } else {
            if (bloodEmitterTypes.has(emitter.type)) configureBloodColors(emitter);
            emitter.update(game.editor.deltaTime * 0.001);
        }
    }
    bloodTime += game.editor.deltaTime * 0.001;
    endSplatFrame();
}
export const reset = function () {
    resetSplats();
    bloodTime = 0;
    activeSplashEmitters = 0;
    globalBody.emitterCount = 0;
    emittersPool = {}
    for (let i = 0; i < emitters.length; i++) {
        let emitter = emitters[i];
        emitter.body = undefined;
        emitter.cleanup();
        emitter.emit = false;
        if(emitter.container){
            const oldContainer = emitter.container;
            emitter.parent = game.editor.textures;
            if(oldContainer.parent) oldContainer.parent.removeChild(oldContainer);
            oldContainer.destroy({children: false});
            delete emitter.container;
        }
        if (!emittersPool[emitter.type]) emittersPool[emitter.type] = [];
        emittersPool[emitter.type].push(emitter);
    }
}
